import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SearchableSelect from '../common/SearchableSelect';
import { bankRulesService } from '../../services/bankRulesService';
import { coaService } from '../../services/coaService';
import { useClient } from '../../context/ClientContext';

const { FiX, FiPlus, FiTrash2, FiAlertCircle } = FiIcons;

const advancedConditionFields = [
  { value: 'bank_text', label: 'Bank Text' },
  { value: 'description', label: 'Description' },
  { value: 'payee', label: 'Payee' },
  { value: 'amount', label: 'Amount' },
  { value: 'category', label: 'Category' },
  { value: 'transaction_type', label: 'Transaction Type' }
];

const advancedTextOperators = [
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does Not Equal' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' }
];

const advancedAmountOperators = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does Not Equal' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' }
];

const isBankOrCardAccount = (account) => {
  const details = `${account.name || ''} ${account.type || ''} ${account.sub_type || ''}`
    .toLowerCase();
  const accountType = String(account.type || '').toLowerCase();

  return (
    (accountType === 'asset' && (
      details.includes('bank') ||
      details.includes('cash') ||
      details.includes('checking') ||
      details.includes('savings')
    )) ||
    (accountType === 'liability' && (
      details.includes('credit card') ||
      details.includes('creditcard')
    ))
  );
};

export default function BankRuleModal({ isOpen, onClose, onSuccess, ruleToEdit = null }) {
  const { activeClient } = useClient();
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [testResults, setTestResults] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    match_type: 'ALL',
    assign_category: '',
    assigned_category_id: '',
    applied_account_ids: [],
    transaction_type: 'All',
    action_transaction_type: 'Expense',
    action_payee: '',
    memo_action: 'keep',
    memo_value: '',
    split_lines: [],
    auto_add: false,
    conditions: [
      { field: 'description', operator: 'contains', value: '' }
    ]
  });

  useEffect(() => {
    if (isOpen) {
      if (ruleToEdit) {
        setFormData({
          name: ruleToEdit.name,
          match_type: ruleToEdit.match_type,
          assign_category: ruleToEdit.assign_category,
          assigned_category_id: ruleToEdit.assigned_category_id || '',
          applied_account_ids: ruleToEdit.applied_account_ids || [],
          transaction_type: ruleToEdit.transaction_type || 'All',
          action_transaction_type: ruleToEdit.action_transaction_type || 'Expense',
          action_payee: ruleToEdit.action_payee || '',
          memo_action: ruleToEdit.memo_action || 'keep',
          memo_value: ruleToEdit.memo_value || '',
          split_lines: ruleToEdit.split_lines || [],
          auto_add: ruleToEdit.auto_add,
          conditions: ruleToEdit.conditions?.length ? ruleToEdit.conditions : [{ field: 'description', operator: 'contains', value: '' }]
        });
      } else {
        setFormData({
          name: '',
          match_type: 'ALL',
          assign_category: '',
          assigned_category_id: '',
          applied_account_ids: [],
          transaction_type: 'All',
          action_transaction_type: 'Expense',
          action_payee: '',
          memo_action: 'keep',
          memo_value: '',
          split_lines: [],
          auto_add: false,
          conditions: [{ field: 'description', operator: 'contains', value: '' }]
        });
      }
      setError(null);
      setTestResults([]);
    }
  }, [isOpen, ruleToEdit]);

  useEffect(() => {
    if (!isOpen) return;

    const loadAccounts = async () => {
      setLoadingAccounts(true);
      try {
        const data = await coaService.getChartOfAccounts(activeClient?.id);
        setAccounts(data);
      } catch (err) {
        setError("Failed to load accounts for this rule.");
      } finally {
        setLoadingAccounts(false);
      }
    };

    loadAccounts();
  }, [isOpen, activeClient?.id]);

  const handleAddCondition = () => {
    setFormData({
      ...formData,
      conditions: [...formData.conditions, { field: 'description', operator: 'contains', value: '' }]
    });
  };

  const handleRemoveCondition = (index) => {
    const newConditions = [...formData.conditions];
    newConditions.splice(index, 1);
    setFormData({ ...formData, conditions: newConditions });
  };

  const handleConditionChange = (index, key, value) => {
    const newConditions = [...formData.conditions];
    newConditions[index][key] = value;
    setFormData({ ...formData, conditions: newConditions });
  };

  const handleAppliedAccountChange = (accountId) => {
    const appliedAccountIds = formData.applied_account_ids.includes(accountId)
      ? formData.applied_account_ids.filter((id) => id !== accountId)
      : [...formData.applied_account_ids, accountId];

    setFormData({ ...formData, applied_account_ids: appliedAccountIds });
  };

  const handleCategoryChange = (categoryId) => {
    const selectedCategory = categoryAccounts.find((account) => account.id === categoryId);

    setFormData({
      ...formData,
      assigned_category_id: categoryId,
      assign_category: selectedCategory?.name || ''
    });
  };

  const handleAddSplit = () => {
    setFormData({
      ...formData,
      split_lines: [...formData.split_lines, { category_id: '', amount: '', memo: '' }]
    });
  };

  const handleSplitChange = (index, key, value) => {
    const newSplits = [...formData.split_lines];
    newSplits[index][key] = value;
    setFormData({ ...formData, split_lines: newSplits });
  };

  const handleRemoveSplit = (index) => {
    const newSplits = [...formData.split_lines];
    newSplits.splice(index, 1);
    setFormData({ ...formData, split_lines: newSplits });
  };

  const handleTestRule = async () => {
    if (!formData.name.trim()) return setError("Rule name is required.");
    if (formData.conditions.some(c => !String(c.value).trim())) {
      return setError("All condition values must be filled out.");
    }

    setTesting(true);
    setError(null);

    try {
      const results = await bankRulesService.testRule(formData);
      setTestResults(results);
    } catch (err) {
      setError(err.message || "Unable to test this rule.");
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) return setError("Rule name is required.");
    if (!formData.assign_category.trim()) return setError("Category assignment is required.");
    if (formData.conditions.some(c => !c.value.trim())) return setError("All condition values must be filled out.");
    if (formData.split_lines.some(line => !line.category_id || Number(line.amount) <= 0)) {
      return setError("Complete every split row with a category and positive amount.");
    }

    setLoading(true);
    setError(null);
    try {
      if (ruleToEdit) {
        await bankRulesService.updateRule(ruleToEdit.id, formData, activeClient?.id);
      } else {
        await bankRulesService.createRule(formData, activeClient?.id);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || "An error occurred while saving the rule.");
    } finally {
      setLoading(false);
    }
  };

  const sourceAccounts = accounts.filter(isBankOrCardAccount);

  const categoryAccounts = accounts.filter((account) => (
    account.type === 'Expense' || account.type === 'Income' || account.type === 'Revenue'
  ));

  const splitTotal = formData.split_lines.reduce(
    (total, line) => total + Number(line.amount || 0),
    0
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#0f172a]/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Automation</p>
            <h2 className="text-xl font-bold text-gray-900">
              {ruleToEdit ? 'Edit Bank Rule' : 'Create Bank Rule'}
            </h2>
            <p className="mt-1 text-xs text-gray-500">Automatically categorize imported transactions.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {error && (
            <div className="p-4 bg-red-50 text-red-600 rounded-xl text-sm font-medium flex items-start gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-lg flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Rule Name</label>
            <input 
              type="text" 
              placeholder="e.g., Amazon Purchases"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-gray-900 font-medium transition-all"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-bold text-gray-700">Applied Accounts</label>
              <span className="text-[11px] text-gray-500">Leave unselected for all accounts</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-32 overflow-y-auto p-3 bg-gray-50 border border-gray-200 rounded-xl">
              {loadingAccounts ? (
                <p className="col-span-full text-xs text-gray-500 text-center py-2">Loading accounts...</p>
              ) : sourceAccounts.length === 0 ? (
                <p className="col-span-full text-xs text-gray-500 text-center py-2">No bank or card accounts found.</p>
              ) : sourceAccounts.map((account) => (
                <label key={account.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.applied_account_ids.includes(account.id)}
                    onChange={() => handleAppliedAccountChange(account.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="truncate">{account.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Apply Rule To</label>
            <SearchableSelect
              options={[
                { id: 'All', name: 'All transactions' },
                { id: 'Money out', name: 'Money out' },
                { id: 'Money in', name: 'Money in' }
              ]}
              value={formData.transaction_type}
              onChange={(value) => setFormData({...formData, transaction_type: value})}
              placeholder="All transactions"
              searchPlaceholder="Search transaction direction..."
              grouped={false}
              menuWidth={360}
              className="[&>button]:bg-gray-50 [&>button]:px-4 [&>button]:py-2.5 [&>button]:text-gray-900"
            />
            <p className="mt-1 text-[11px] text-gray-500">Choose which bank transaction direction can trigger this rule.</p>
          </div>

          <div className="p-5 bg-gray-50 rounded-xl border border-gray-100 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-bold text-gray-700">Conditions</label>
              <SearchableSelect
                options={[
                  { id: 'ALL', name: 'Match ALL conditions' },
                  { id: 'ANY', name: 'Match ANY conditions' }
                ]}
                value={formData.match_type}
                onChange={(value) => setFormData({...formData, match_type: value})}
                placeholder="Match ALL conditions"
                searchPlaceholder="Search match type..."
                grouped={false}
                menuWidth={260}
                className="w-56 [&>button]:bg-white [&>button]:px-3 [&>button]:py-1.5 [&>button]:text-gray-700"
              />
            </div>

            {formData.conditions.map((condition, index) => (
              <div key={index} className="flex flex-col sm:flex-row gap-3">
                <SearchableSelect
                  options={advancedConditionFields.map((field) => ({
                    id: field.value,
                    name: field.label
                  }))}
                  value={condition.field}
                  onChange={(value) => handleConditionChange(index, 'field', value)}
                  placeholder="Description"
                  searchPlaceholder="Search condition fields..."
                  grouped={false}
                  menuWidth={260}
                  className="flex-1 [&>button]:bg-white [&>button]:px-3 [&>button]:py-2.5 [&>button]:text-gray-700"
                />
                
                <SearchableSelect
                  options={(condition.field === 'amount' ? advancedAmountOperators : advancedTextOperators).map((operator) => ({
                    id: operator.value,
                    name: operator.label
                  }))}
                  value={condition.operator}
                  onChange={(value) => handleConditionChange(index, 'operator', value)}
                  placeholder="Contains"
                  searchPlaceholder="Search operators..."
                  grouped={false}
                  menuWidth={260}
                  className="flex-1 [&>button]:bg-white [&>button]:px-3 [&>button]:py-2.5 [&>button]:text-gray-700"
                />

                {condition.field === 'transaction_type' ? (
                  <SearchableSelect
                    options={[
                      { id: 'Money in', name: 'Money in' },
                      { id: 'Money out', name: 'Money out' }
                    ]}
                    value={condition.value}
                    onChange={(value) => handleConditionChange(index, 'value', value)}
                    placeholder="Select transaction type"
                    searchPlaceholder="Search transaction types..."
                    grouped={false}
                    menuWidth={280}
                    className="flex-[2] [&>button]:bg-white [&>button]:px-3 [&>button]:py-2.5 [&>button]:text-gray-900"
                  />
                ) : (
                  <input 
                    type={condition.field === 'amount' ? 'number' : 'text'}
                    step={condition.field === 'amount' ? '0.01' : undefined}
                    placeholder="Value"
                    value={condition.value}
                    onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                    className="flex-[2] px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-sm font-medium text-gray-900"
                  />
                )}

                {formData.conditions.length > 1 && (
                  <button 
                    type="button" 
                    onClick={() => handleRemoveCondition(index)}
                    className="w-10 h-10 flex-shrink-0 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                )}
              </div>
            ))}

            <button 
              type="button" 
              onClick={handleAddCondition}
              className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-2"
            >
              <SafeIcon icon={FiPlus} /> Add Condition
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Assign to Category</label>
              <SearchableSelect
                options={categoryAccounts}
                value={formData.assigned_category_id}
                onChange={handleCategoryChange}
                placeholder="Select a Chart of Accounts category"
                searchPlaceholder="Search categories..."
                emptyMessage="No matching categories"
                getGroup={(account) => account.type || 'Other'}
                getLabel={(account) => `${account.name} (${account.type})`}
                getSearchText={(account) => [
                  account.name,
                  account.code,
                  account.type,
                  account.sub_type,
                  account.description
                ]}
                menuWidth={420}
                className="mb-2 [&>button]:bg-gray-50 [&>button]:px-4 [&>button]:py-2.5 [&>button]:text-gray-900"
              />
              <input 
                type="text" 
                placeholder="e.g., Office Supplies"
                value={formData.assign_category}
                onChange={(e) => setFormData({...formData, assign_category: e.target.value, assigned_category_id: ''})}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-gray-900 font-medium transition-all"
                list="categories"
              />
              <datalist id="categories">
                <option value="Office Supplies" />
                <option value="Meals & Entertainment" />
                <option value="Rent or Lease" />
                <option value="Dues & Subscriptions" />
                <option value="Travel" />
              </datalist>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Automation</label>
              <label className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                <div className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={formData.auto_add}
                    onChange={(e) => setFormData({...formData, auto_add: e.target.checked})}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                </div>
                <div>
                  <span className="block text-sm font-bold text-gray-900">Auto-add</span>
                  <span className="block text-[11px] text-gray-500 font-medium leading-tight mt-0.5">Automatically clear transactions</span>
                </div>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Posting Type</label>
              <SearchableSelect
                options={[
                  { id: 'Expense', name: 'Expense' },
                  { id: 'Income', name: 'Income' },
                  { id: 'Transfer', name: 'Transfer' }
                ]}
                value={formData.action_transaction_type}
                onChange={(value) => setFormData({...formData, action_transaction_type: value})}
                placeholder="Expense"
                searchPlaceholder="Search posting type..."
                grouped={false}
                menuWidth={320}
                className="[&>button]:bg-gray-50 [&>button]:px-4 [&>button]:py-2.5 [&>button]:text-gray-900"
              />
              <p className="mt-1 text-[11px] text-gray-500">Set the accounting result for transactions that match this rule.</p>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Bank Memo</label>
              <SearchableSelect
                options={[
                  { id: 'keep', name: 'Keep existing memo' },
                  { id: 'replace', name: 'Replace memo' }
                ]}
                value={formData.memo_action}
                onChange={(value) => setFormData({...formData, memo_action: value})}
                placeholder="Keep existing memo"
                searchPlaceholder="Search memo action..."
                grouped={false}
                menuWidth={320}
                className="[&>button]:bg-gray-50 [&>button]:px-4 [&>button]:py-2.5 [&>button]:text-gray-900"
              />
              {formData.memo_action === 'replace' && (
                <input
                  type="text"
                  placeholder="Replacement memo"
                  value={formData.memo_value}
                  onChange={(e) => setFormData({...formData, memo_value: e.target.value})}
                  className="w-full px-4 py-2.5 mt-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-gray-900 font-medium transition-all"
                />
              )}
            </div>
          </div>

          <div className="p-5 bg-gray-50 rounded-xl border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-bold text-gray-700">Split Transactions</label>
                <p className="text-[11px] text-gray-500 mt-1">Allocate one transaction across multiple categories.</p>
              </div>
              <button
                type="button"
                onClick={handleAddSplit}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <SafeIcon icon={FiPlus} /> Add split
              </button>
            </div>

            {formData.split_lines.map((line, index) => (
              <div key={index} className="grid grid-cols-1 sm:grid-cols-[1.5fr_1fr_1.5fr_auto] gap-2 mt-3">
                <SearchableSelect
                  options={categoryAccounts}
                  value={line.category_id}
                  onChange={(value) => handleSplitChange(index, 'category_id', value)}
                  placeholder="Select category"
                  searchPlaceholder="Search categories..."
                  emptyMessage="No matching categories"
                  getGroup={(account) => account.type || 'Other'}
                  getLabel={(account) => account.name}
                  getSearchText={(account) => [
                    account.name,
                    account.code,
                    account.type,
                    account.sub_type
                  ]}
                  menuWidth={380}
                  className="[&>button]:bg-white [&>button]:px-3 [&>button]:py-2.5 [&>button]:text-gray-700"
                />
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Amount"
                  value={line.amount}
                  onChange={(e) => handleSplitChange(index, 'amount', e.target.value)}
                  className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900"
                />
                <input
                  type="text"
                  placeholder="Split memo"
                  value={line.memo}
                  onChange={(e) => handleSplitChange(index, 'memo', e.target.value)}
                  className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveSplit(index)}
                  className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
            ))}

            {formData.split_lines.length > 0 && (
              <p className="text-xs text-gray-500 mt-3">
                Split total: <strong>${splitTotal.toFixed(2)}</strong>
              </p>
            )}
          </div>

          <div className="p-5 bg-blue-50 border border-blue-100 rounded-xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-blue-900">Test Rule</h3>
                <p className="text-xs text-blue-800/70 mt-1">Show matching transactions before saving.</p>
              </div>
              <button
                type="button"
                onClick={handleTestRule}
                disabled={testing}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {testing ? 'Testing...' : 'Test Rule'}
              </button>
            </div>

            {testResults.length > 0 && (
              <div className="mt-4 space-y-2">
                {testResults.map((transaction) => (
                  <div key={transaction.id} className="bg-white border border-blue-100 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs font-semibold text-gray-800">{transaction.date}</span>
                      <span className="text-xs font-bold text-gray-800">
                        ${Math.max(Number(transaction.debit || 0), Number(transaction.credit || 0)).toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1 truncate">
                      {transaction.description || 'No description'}
                    </p>
                    <p className="text-[11px] text-emerald-600 font-semibold mt-1">
                      {transaction.match_status}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-5 bg-gray-50 rounded-xl border border-gray-100">
            <label className="block text-sm font-bold text-gray-700 mb-2">Preview</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <span className="block text-[11px] text-gray-500">Posting Type</span>
                <strong className="text-gray-900">{formData.action_transaction_type}</strong>
              </div>
              <div>
                <span className="block text-[11px] text-gray-500">Category</span>
                <strong className="text-gray-900">{formData.assign_category || 'Not selected'}</strong>
              </div>
              <div>
                <span className="block text-[11px] text-gray-500">Status</span>
                <strong className={formData.auto_add ? 'text-emerald-600' : 'text-amber-600'}>
                  {formData.auto_add ? 'Auto Posted' : 'Suggest Only'}
                </strong>
              </div>
            </div>
          </div>

          <div className="pt-4 flex gap-3 border-t border-gray-100">
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 py-3 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="flex-[2] py-3 text-sm font-bold text-white bg-[#2563eb] hover:bg-blue-700 rounded-xl shadow-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                ruleToEdit ? 'Save Changes' : 'Create Rule'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}